import React, { useState } from 'react';
import Dialog from './Dialog';
import Segmented from './Segmented';
import SectionSelect from './SectionSelect';
import useDialogMutation from '../hooks/useDialogMutation';
import { CATEGORIES, FREQUENCIES, centsToInput, parseAmountToCents, parseYmd } from '../utils/expenseMath';

const FREQUENCY_CHOICES = FREQUENCIES.map((item) => ({ id: item.id, title: item.label }));

function ExpenseDialog({ title, submitLabel, expense, defaultCategory, onSubmit, onClose }) {
  const [name, setName] = useState(expense?.name || '');
  const [category, setCategory] = useState(expense?.category || defaultCategory || '');
  const [amount, setAmount] = useState(expense ? centsToInput(expense.amountCents) : '');
  const [frequency, setFrequency] = useState(expense?.frequency || 'monthly');
  const [dueDate, setDueDate] = useState(expense?.anchorDate || '');
  const [errors, setErrors] = useState({});
  const { run, saving, error: saveError } = useDialogMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const next = {};
    const trimmed = name.trim();
    const amountCents = parseAmountToCents(amount);
    if (!trimmed) next.name = 'Name is required.';
    if (!category) next.category = 'Choose a category.';
    if (amountCents === null) next.amount = 'Enter an amount greater than 0, like 240.00.';
    if (!parseYmd(dueDate)) next.dueDate = 'Enter a valid due date.';
    setErrors(next);
    if (Object.keys(next).length) return;
    await run(() => onSubmit({
      name: trimmed, category, amountCents, frequency, anchorDate: dueDate
    }), onClose);
  };

  return (
    <Dialog title={title} onClose={onClose} locked={saving}>
      <form onSubmit={handleSubmit} noValidate>
        <fieldset className="burn-fieldset" disabled={saving}>
          <label className="dialog-label" htmlFor="expense-name">Name</label>
          <input
            id="expense-name"
            className="dialog-input"
            value={name}
            maxLength={80}
            autoFocus
            aria-invalid={Boolean(errors.name)}
            onChange={(event) => setName(event.target.value)}
          />
          {errors.name && <p className="dialog-error">{errors.name}</p>}

          <span className="dialog-label field-gap">Category</span>
          <Segmented
            label="Category"
            className="segmented-fill"
            options={CATEGORIES}
            value={category}
            onChange={setCategory}
          />
          {errors.category && <p className="dialog-error">{errors.category}</p>}

          <label className="dialog-label field-gap" htmlFor="expense-amount">Amount (USD)</label>
          <input
            id="expense-amount"
            className="dialog-input"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            aria-invalid={Boolean(errors.amount)}
            onChange={(event) => setAmount(event.target.value)}
          />
          {errors.amount && <p className="dialog-error">{errors.amount}</p>}

          <span className="dialog-label field-gap" id="expense-frequency-label">Billing frequency</span>
          <SectionSelect
            sections={FREQUENCY_CHOICES}
            value={frequency}
            onChange={setFrequency}
            labelId="expense-frequency-label"
          />

          <label className="dialog-label field-gap" htmlFor="expense-due">Next due date</label>
          <input
            id="expense-due"
            className="dialog-input"
            type="date"
            value={dueDate}
            aria-invalid={Boolean(errors.dueDate)}
            onChange={(event) => setDueDate(event.target.value)}
          />
          {errors.dueDate
            ? <p className="dialog-error">{errors.dueDate}</p>
            : <p className="dialog-hint">Later due dates are projected from this date.</p>}
        </fieldset>
        {saveError && <p className="dialog-error" role="alert">{saveError}</p>}

        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export default ExpenseDialog;